import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";

import log from "@/helpers/log";
import { DOWNLOADS_MODULE_KEY, TOAST_TYPES } from "@/utils/enums";
import store from "@/vuexStore";

import projectModule, { type ProjectUsersExportStatusResponse } from "./project";
import userModule from "./user";

export type ExportFilters = {
  userIdOrAddress?: string;
  lastLoginFrom?: string;
  lastLoginTo?: string;
};

export type DownloadStatus = "exporting" | "completed" | "failed" | "expired";

// Kept intentionally generic so future export types (verifiers, billing, ...)
// can reuse the same registry by adding a new `type` discriminator.
export type DownloadEntry = {
  id: string; // exportId, also the dedupe/upsert key
  type: "users-csv";
  label: string;
  projectId: string;
  projectName: string;
  teamId: number;
  // Snapshot of the filters used to start the export so retry/export-again can
  // re-run it from the overlay without the originating page being mounted.
  filters: ExportFilters;
  status: DownloadStatus;
  processedRows: number;
  totalRows: number;
  downloadUrl?: string;
  downloadExpiresAt?: string;
  startedAt: number;
  completedAt?: number;
  errorMessage?: string;
};

const STORAGE_KEY_PREFIX = "user_downloads";
const TERMINAL_STATUSES = new Set<string>(["completed", "failed", "expired"]);
const POLL_INTERVAL_MS = 2500;
const POLL_INTERVAL_SLOW_MS = 5000;
const LARGE_EXPORT_ROWS = 10000;
const EXPIRE_SWEEP_MS = 30000;
// Retry transient status-check failures with backoff before giving up.
const MAX_POLL_RETRIES = 4;
const POLL_BACKOFF_BASE_MS = 2000;
const POLL_BACKOFF_MAX_MS = 30000;

// Per-entry generation counter lives outside reactive state; bumping it makes any
// in-flight poll loop for that id abandon (mirrors the stale-guard the button had).
const pollGenerations = new Map<string, number>();

// Tracks the currently authenticated user so every read/write uses a scoped key.
// Set by hydrate() on mount and never cleared — only logout (user.ts) needs to
// remove the storage key, which it does via clearDownloadsStorage() directly.
let activeUserId = "";

function storageKey(): string {
  return activeUserId ? `${STORAGE_KEY_PREFIX}:${activeUserId}` : STORAGE_KEY_PREFIX;
}

/**
 * Remove the persisted download entries for `userId` from localStorage.
 * Exported as a plain function (not a Vuex action) so that user.ts can call it
 * during logout without creating a circular module dependency.
 */
export function clearDownloadsStorage(userId: string): void {
  if (!userId) return;
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}:${userId}`);
  } catch {
    // ignore — storage may be unavailable (private browsing, quota errors, etc.)
  }
  // Reset the active-user sentinel so persistEntries() becomes a no-op for
  // any poll iteration that slips past the generation check after logout.
  activeUserId = "";
}

// Identifies a specific export request: a given project with a given filter set.
// Multiple downloads per project are allowed; only an identical concurrent export
// is treated as a duplicate.
function exportKey(projectId: string, filters: ExportFilters): string {
  return [projectId, filters.userIdOrAddress ?? "", filters.lastLoginFrom ?? "", filters.lastLoginTo ?? ""].join("::");
}
// Single sweep loop for the whole list (replaces the per-instance expiry ticker).
let sweepRunning = false;

// Per-request token so a finishing create POST drops only its own pending entry.
let pendingTokenSeq = 0;

// Bumped by clearAll() so any in-flight startUsersExport that resolves after a
// clear can detect it is stale and discard its result instead of re-inserting.
let clearGeneration = 0;

function nextGeneration(id: string): number {
  const generation = (pollGenerations.get(id) ?? 0) + 1;
  pollGenerations.set(id, generation);
  return generation;
}

function currentGeneration(id: string): number {
  return pollGenerations.get(id) ?? 0;
}

// Bumping the generation is enough to stop a loop; the pending sleep resolves once
// more, sees a stale generation, and returns without another network call.
function invalidatePolling(id: string): void {
  nextGeneration(id);
  pollGenerations.delete(id);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function readStorage(userId: string): DownloadEntry[] {
  if (!userId) return [];
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}:${userId}`);
  } catch (error) {
    log.error("Error reading user downloads from storage", error);
    return [];
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as DownloadEntry[];
    return Array.isArray(parsed) ? parsed.filter((entry) => entry && typeof entry.id === "string") : [];
  } catch {
    return [];
  }
}

function persistEntries(entries: DownloadEntry[]): void {
  // Skip writes when no user is active so we never mix data across accounts.
  if (!activeUserId) return;
  try {
    localStorage.setItem(storageKey(), JSON.stringify(entries));
  } catch (error) {
    log.error("Error persisting user downloads", error);
  }
}

function isPastExpiry(entry: DownloadEntry, now: number): boolean {
  if (!entry.downloadExpiresAt) return false;
  const expiresAt = new Date(entry.downloadExpiresAt).getTime();
  return !Number.isNaN(expiresAt) && expiresAt <= now;
}

// @ts-expect-error decorator is picking the wrong overload as its type
@Module({
  namespaced: true,
  name: DOWNLOADS_MODULE_KEY,
  store,
  dynamic: true,
})
class DownloadsModule extends VuexModule {
  entries: DownloadEntry[] = [];

  // In-flight create POSTs, keyed by project + filters with a per-request token.
  // Reactive so `isExporting`/the button cover the window before the entry exists;
  // the token lets each request clear only its own entry.
  pending: { token: number; key: string }[] = [];

  // All downloads across projects, newest first, for the global overlay.
  public get allEntriesSorted(): DownloadEntry[] {
    return [...this.entries].sort((a, b) => b.startedAt - a.startedAt);
  }

  public get activeCount(): number {
    return this.entries.filter((entry) => entry.status === "exporting").length;
  }

  // True when an export with this exact project + filters is in flight, including the
  // create-POST window before the entry exists (tracked via `pending`).
  public get isExporting() {
    return (projectId: string, filters: ExportFilters): boolean => {
      const key = exportKey(projectId, filters);
      if (this.pending.some((item) => item.key === key)) return true;
      return this.entries.some((entry) => entry.status === "exporting" && exportKey(entry.projectId, entry.filters) === key);
    };
  }

  @Mutation
  addPending(entry: { token: number; key: string }) {
    this.pending.push(entry);
  }

  @Mutation
  removePending(token: number) {
    this.pending = this.pending.filter((item) => item.token !== token);
  }

  @Mutation
  clearPending() {
    this.pending = [];
  }

  @Mutation
  setEntries(entries: DownloadEntry[]) {
    this.entries = entries;
    persistEntries(this.entries);
  }

  @Mutation
  upsertEntry(entry: DownloadEntry) {
    const idx = this.entries.findIndex((item) => item.id === entry.id);
    if (idx === -1) {
      this.entries.push(entry);
    } else {
      this.entries.splice(idx, 1, entry);
    }
    persistEntries(this.entries);
  }

  @Mutation
  removeEntry(id: string) {
    this.entries = this.entries.filter((entry) => entry.id !== id);
    persistEntries(this.entries);
  }

  @Mutation
  applyStatusUpdate({ id, status }: { id: string; status: ProjectUsersExportStatusResponse }) {
    const idx = this.entries.findIndex((item) => item.id === id);
    if (idx === -1) return;
    const entry: DownloadEntry = { ...this.entries[idx] };
    entry.processedRows = status.processed_rows ?? entry.processedRows;
    entry.totalRows = status.total_rows ?? entry.totalRows;

    if (!TERMINAL_STATUSES.has(status.status)) {
      entry.status = "exporting";
    } else if (status.status === "completed") {
      entry.downloadUrl = status.download_url ?? "";
      entry.downloadExpiresAt = status.download_url_expires_at ?? "";
      // No download URL yet means it is not truly ready; keep polling.
      if (entry.downloadUrl) {
        entry.status = "completed";
        entry.completedAt = entry.completedAt ?? Date.now();
      } else {
        entry.status = "exporting";
      }
    } else if (status.status === "expired") {
      entry.status = "expired";
    } else {
      entry.status = "failed";
      entry.errorMessage = status.error || "Export failed";
    }

    this.entries.splice(idx, 1, entry);
    persistEntries(this.entries);
  }

  @Mutation
  sweepExpired() {
    const now = Date.now();
    let changed = false;
    this.entries = this.entries.map((entry) => {
      if (entry.status === "completed" && isPastExpiry(entry, now)) {
        changed = true;
        return { ...entry, status: "expired" as DownloadStatus };
      }
      return entry;
    });
    if (changed) persistEntries(this.entries);
  }

  @Action
  async startUsersExport({
    projectId,
    projectName,
    teamId,
    filters,
    label,
  }: {
    projectId: string;
    projectName?: string;
    teamId: number;
    filters: ExportFilters;
    label?: string;
  }): Promise<void> {
    // Dedupe only identical concurrent exports (same project + filters), so a
    // project can have several downloads in flight/kept with different filters.
    // The triggering button is disabled while `isExporting`, so this is just a
    // store-level guard for same-tick/programmatic double-invocations.
    const key = exportKey(projectId, filters);
    if (this.isExporting(projectId, filters)) return;
    const token = (pendingTokenSeq += 1);
    // Snapshot the generation so we can detect a clearAll() that fires while the
    // create POST is in-flight and discard the result instead of re-inserting it.
    const capturedClearGen = clearGeneration;
    this.addPending({ token, key });
    const startedAt = Date.now();
    // Snapshot the project name now so the overlay can label the row on any page,
    // even after projects for that team are no longer loaded.
    const resolvedProjectName = projectName || projectModule.projectGetter(teamId, projectId)?.name || projectId;
    try {
      const res = await projectModule.exportProjectUsers({
        projectId,
        teamId,
        userIdOrAddress: filters.userIdOrAddress,
        lastLoginFrom: filters.lastLoginFrom,
        lastLoginTo: filters.lastLoginTo,
      });
      // clearAll() was called while the POST was in-flight; discard the result so
      // the entry never re-appears and no poll loop is started for a cleared export.
      if (clearGeneration !== capturedClearGen) return;
      this.upsertEntry({
        id: res.export_id,
        type: "users-csv",
        label: label || "Users export",
        projectId,
        projectName: resolvedProjectName,
        teamId,
        filters: { ...filters },
        status: "exporting",
        processedRows: 0,
        totalRows: res.total_rows ?? 0,
        startedAt,
      });
      // Handle an already-terminal initial status without waiting for a poll.
      this.applyStatusUpdate({
        id: res.export_id,
        status: { export_id: res.export_id, status: res.status, processed_rows: 0, total_rows: res.total_rows ?? 0 },
      });
      const created = this.entries.find((entry) => entry.id === res.export_id);
      if (created?.status === "exporting") void this.pollUntilDone(res.export_id);
      void this.ensureExpireSweep();
    } catch (error) {
      const message = (error as { message?: string })?.message || "Failed to start export";
      log.error("Error starting users export", error);
      userModule.setToastMessage({ type: TOAST_TYPES.ERROR, message });
    } finally {
      this.removePending(token);
    }
  }

  // Self-contained poll loop. `this` stays valid across awaits, so no timer
  // callbacks or module-singleton forward references are needed. Each iteration
  // re-checks the generation so a cleared/superseded export abandons cleanly.
  @Action
  async pollUntilDone(id: string): Promise<void> {
    const generation = nextGeneration(id);
    // Consecutive poll errors; reset on success, fail once past the retry budget.
    let consecutiveErrors = 0;
    // Times the API reported "completed" but omitted the download_url. applyStatusUpdate
    // keeps the entry as "exporting" in that case so polling continues, but without a
    // budget this path would loop forever. Share the same MAX_POLL_RETRIES limit.
    let completedWithoutUrlCount = 0;
    while (currentGeneration(id) === generation) {
      const entry = this.entries.find((item) => item.id === id);
      if (!entry || entry.status !== "exporting") return;
      const delay = entry.totalRows > LARGE_EXPORT_ROWS ? POLL_INTERVAL_SLOW_MS : POLL_INTERVAL_MS;

      await sleep(delay);
      if (currentGeneration(id) !== generation) return;
      const current = this.entries.find((item) => item.id === id);
      if (!current || current.status !== "exporting") return;
      try {
        const status = await projectModule.getProjectUsersExportStatus({
          projectId: current.projectId,
          teamId: current.teamId,
          exportId: id,
        });
        if (currentGeneration(id) !== generation) return;
        consecutiveErrors = 0;
        this.applyStatusUpdate({ id, status });

        // When the API says "completed" but provides no download_url, applyStatusUpdate
        // intentionally leaves the entry as "exporting" to keep polling. Enforce a
        // retry budget so a permanently missing URL does not cause an infinite loop.
        if (status.status === "completed" && !status.download_url) {
          completedWithoutUrlCount += 1;
          if (completedWithoutUrlCount >= MAX_POLL_RETRIES) {
            this.applyStatusUpdate({
              id,
              status: {
                export_id: id,
                status: "failed",
                processed_rows: current.processedRows,
                total_rows: current.totalRows,
                error: "Export completed but no download URL was provided",
              },
            });
            return;
          }
        } else {
          completedWithoutUrlCount = 0;
        }

        // A finished job needs expiring; restart the sweep if it wound down.
        void this.ensureExpireSweep();
      } catch (error) {
        if (currentGeneration(id) !== generation) return;
        consecutiveErrors += 1;
        log.error(`Error polling users export status (attempt ${consecutiveErrors}/${MAX_POLL_RETRIES})`, error);
        if (consecutiveErrors >= MAX_POLL_RETRIES) {
          this.applyStatusUpdate({
            id,
            status: {
              export_id: id,
              status: "failed",
              processed_rows: current.processedRows,
              total_rows: current.totalRows,
              error: (error as { message?: string })?.message || "Failed to check export status",
            },
          });
          return;
        }
        // Exponential backoff before retrying.
        const backoff = Math.min(POLL_BACKOFF_BASE_MS * 2 ** (consecutiveErrors - 1), POLL_BACKOFF_MAX_MS);
        await sleep(backoff);
        if (currentGeneration(id) !== generation) return;
      }
    }
  }

  // Rehydrate persisted downloads on app load: expire stale links and resume
  // polling for anything still in flight. Replaces the per-mount resume logic.
  // Must receive the authenticated userId so reads/writes use a user-scoped
  // localStorage key and never expose one account's exports to another.
  @Action
  hydrate(userId: string): void {
    if (!userId) return;
    activeUserId = userId;
    const now = Date.now();
    const restored = readStorage(userId).map((entry) =>
      entry.status === "completed" && isPastExpiry(entry, now) ? { ...entry, status: "expired" as DownloadStatus } : entry
    );
    this.setEntries(restored);
    restored.forEach((entry) => {
      if (entry.status === "exporting") void this.pollUntilDone(entry.id);
    });
    void this.ensureExpireSweep();
  }

  @Action
  async ensureExpireSweep(): Promise<void> {
    if (sweepRunning) return;
    sweepRunning = true;
    while (sweepRunning) {
      await sleep(EXPIRE_SWEEP_MS);
      this.sweepExpired();
      // Keep sweeping while entries are still completed or exporting; stopping on
      // "no completed" alone would kill the sweep mid-export.
      if (!this.entries.some((entry) => entry.status === "completed" || entry.status === "exporting")) {
        sweepRunning = false;
        return;
      }
    }
  }

  @Action
  clearOne(id: string): void {
    invalidatePolling(id);
    this.removeEntry(id);
  }

  @Action
  clearAll(): void {
    this.entries.forEach((entry) => invalidatePolling(entry.id));
    // Bump the generation so any in-flight startUsersExport create POST that
    // resolves after this point discards its result rather than re-inserting it.
    clearGeneration += 1;
    // setEntries([]) → persistEntries([]) will write the empty state to localStorage.
    // Do NOT clear activeUserId here: this action is also called from the Downloads
    // overlay "Clear all" button while the user is still logged in. Blanking
    // activeUserId would make every subsequent persistEntries() a no-op, so new
    // exports started after clearing would never be written to localStorage and
    // would be lost on reload. activeUserId is only cleared on logout, which is
    // handled by user.ts calling clearDownloadsStorage() directly.
    this.setEntries([]);
    // Drop pending create POSTs too, else the button stays stuck on "Exporting..."
    // with no row to explain it. A later identical export still dedupes on its token.
    this.clearPending();
  }
}

const downloadsModule = getModule(DownloadsModule);

export default downloadsModule;
